import { Link } from "@inertiajs/react";
import Callout from "@/components/help/Callout";
import { contactMailto } from "@/lib/contact";
import Screenshot from "@/components/help/Screenshot";
import MilestonesCard from "@/components/schools/MilestonesCard";
import { SCHOOL_MILESTONES } from "@/help/fixtures";

export default function SettingSchoolMilestones() {
  return (
    <>
      <p>
        School rewards come right after your students and teachers in the setup guide — before the info and take-home sheets, which list them. A <strong>school reward</strong> is something the whole school earns together when they hit a goal — pizza party at $1,000 raised, principal-gets-pied at $5,000, that kind of thing. They're optional, but they make the read-a-thon a lot more fun.
      </p>
      <p>
        These are different from <Link href="/help/articles/setting-student-prizes">student prizes</Link> (which each student earns on their own) and <Link href="/help/articles/setting-classroom-goals-and-milestones">classroom rewards</Link> (which only that classroom earns).
      </p>

      <h2>How they show up</h2>
      <p>
        Once you've set rewards, they appear on every dashboard — yours, every teacher's, every parent's — as a list with a progress bar and a locked/unlocked icon.
      </p>
      <Screenshot caption="The rewards card on every dashboard">
        <MilestonesCard milestones={SCHOOL_MILESTONES} />
      </Screenshot>

      <h2>Adding a reward</h2>
      <p>
        From your dashboard sidebar, click <strong>School rewards</strong>. If you've set a goal and have no rewards yet, we suggest three sized to it — a quick first win, a halfway treat and a big finish — which you can change before adding. Or click <strong>Add a reward</strong> for a quick form.
      </p>
      <p>
        Name the prize, pick whether it's counted by <strong>money raised</strong> or <strong>minutes read</strong> across the whole school, and set the amount it unlocks at. Each reward shows how close the school is. Some examples:
      </p>
      <ul>
        <li>Pizza party at <strong>$1,000</strong></li>
        <li>Pajama day at <strong>$2,500</strong></li>
        <li>Principal gets pied at <strong>$5,000</strong></li>
        <li>Whole-school silent reading hour at <strong>10,000 minutes</strong></li>
      </ul>
      <p>
        For the name, use language that gets a student excited — "extra recess" beats "30 extra minutes outside." Save and it appears everywhere immediately, listed smallest first: money rewards, then minute rewards.
      </p>
      <p>
        Have a kind of reward in mind that PagePledge doesn't offer? <a href={contactMailto("Feature Request")}>Email us a feature request</a>. The same link is under your list of rewards.
      </p>

      <h2>Unlocking happens on its own</h2>
      <p>
        As soon as the school crosses the number, the reward flips to unlocked across every dashboard and every donation page. There's no button to push.
      </p>
      <Callout variant="note" title="Once unlocked, it stays unlocked">
        <p>
          If a refund later drops the total below the line, the reward doesn't lock back up. We'd rather not pull back a "you earned it!" celebration from a school full of students.
        </p>
      </Callout>

      <p>
        You can add, remove, or change rewards any time until the campaign finishes. After that they're final, along with the rest of the results.
      </p>

      <h2>Top classroom</h2>
      <p>
        A school reward can also go to the classroom that reads or raises the most. When you add a reward, choose <strong>Top classroom</strong> under <strong>How it's won</strong>. Then pick who competes:
      </p>
      <ul>
        <li><strong>Each division</strong>: every division gets its own winner, so a Grade 2 class isn't up against Grade 6.</li>
        <li><strong>Whole school</strong>: one winner across every classroom, whatever its division.</li>
      </ul>
      <p>
        You can add one of each, for example an ice cream party for the top class in each division and a pizza lunch for the top class in the school. Then pick how classrooms are compared:
      </p>
      <ul>
        <li><strong>Average per student</strong>: the class total divided by the number of students, so small and large classes compete fairly. Then choose who counts: <strong>Every student</strong> (students who haven't read count as 0, so the class does best when everyone joins in) or <strong>Students who took part</strong> (only students who've read count, so a few keen readers can carry a class).</li>
        <li><strong>Class total</strong>: the whole class's minutes or dollars. Bigger classes have an edge.</li>
      </ul>
      <p>
        Each classroom has a <strong>Division</strong> on its Edit page. New classrooms start in the division for their grade (K–3 is Division 1, 4–6 is Division 2, and so on), and you can change it for split classes or your own groupings. For an each-division prize, every division gets its own winner, and if you leave every division blank, the whole school competes together. Ties all win.
      </p>
      <p>
        The minimum to qualify starts at 1, which means "read at all." It's what the class's average (or total) has to reach to win, so a class where nobody has read yet can't win at 0. It never leaves a student out of the average. While reading is on, your School rewards page shows who's leading each division. Teachers and families see their own class's place, like "2nd of 4 classes", but never other classrooms' names. When the campaign finishes, the winning classrooms appear on the <strong>Prize report</strong>, where you can mark each prize handed out.
      </p>

      <h2>What's next</h2>
      <p>
        Once your rewards are in, the setup guide moves on to getting the word out to teachers and families. The campaign goes live automatically on your start date.
      </p>
      <ul>
        <li><Link href="/help/articles/setting-student-prizes">Student prizes</Link> — each student earns their own.</li>
        <li><Link href="/help/articles/setting-classroom-goals-and-milestones">Classroom rewards</Link>.</li>
        <li><Link href="/help/articles/campaign-lifecycle">The four stages of a campaign</Link>.</li>
      </ul>
    </>
  );
}
